import {
  ChatMessage,
  ChatMessageAttributes,
  ChatUserAttributes,
} from '../Chat';

export interface Counter {
  counter_id: string;
  product_id: string;
  counter_vendor: {
    vendor_id: string;
    vendor_name: string;
    is_new_vendor: boolean;
  };
  last_message?: {
    content: string;
    updated_at: string;
  };
  fn_contacts: VendorContact[];
  created_at: string;
  updated_at: string;
  prev_sample_status: string;
  prev_sample_shipped_at: string | null;
  prev_sample_tracking_to_vendor: string | null;
  prev_sample_vendor_received_at: string | null;
  counter_sample_status: string;
  counter_sample_shipped_at: string;
  counter_sample_tracking: string;
  counter_sample_received_at: string;
  counter_quote_status: string;
  prev_counter_quote_status?: string;
  request_counter_quote_to_vendor: string;
  priority: string;
  vendor_contacts: VendorContact[];
  reasons_for_request: string[];
  fn_comments: Comment[];
  counter_quote?: CounterQuote;
  target_price: number;
  savings_per_year: string;
  vendor_approved_at: string | null;
  vendor_approved_by: string | null;
  vendor_rejected_at: string | null;
  vendor_rejected_by: string | null;
  vendor_rejected_reason: string[];
  request_approved_at: string | null;
  request_approved_by: string | null;
  request_rejected_at: string | null;
  request_rejected_by: string | null;
  request_rejected_comment: string | null;
  po_written: boolean;
  photos: string[];
  product_description: string;
  style_color: string;
  display_name: string;
  division: 'womens' | 'kids' | string;
  colors_approved: string | null;
  baseline_jr_cost?: number;
  baseline_plus_cost?: number;
}

export interface VendorContact {
  id: string;
  email: string;
  full_name: string;
}

interface Comment {
  name: string;
  content: string;
  timestamp: string;
}

export interface CounterFabric {
  name: string;
  percentage: number;
}

// Note: 'backing' and 'coating' are legacy types - not available for new entries but supported for existing data
export type SecondaryFabricType =
  | 'lining'
  | 'contrast'
  | 'filling'
  | 'backing'
  | 'coating';

export type GarmentPart = 'top' | 'bottom' | 'other';

export interface SecondaryFabric {
  type: SecondaryFabricType;
  instanceIndex: number;
  content: CounterFiberContent[];
  weight?: number | string;
  hasPolyurethaneCoating?: boolean;
}

export interface GarmentFabricDetails {
  garmentIndex: number;
  garmentPart: GarmentPart;
  fabricContentList: CounterFiberContent[];
  fabricWeight?: string;
  fabricCodeName?: string;
  isFabricWeightNotApplicable?: boolean;
  hasPolyurethaneCoating?: boolean;
  secondaryFabrics: SecondaryFabric[];
}

export interface CounterQuote {
  coo: string;
  prev_coo: string;
  mcq: number;
  moq: number | '';
  ranges: CounterQuoteRange[];
  fabric_type: string;
  fabric_weight: string;
  prev_fabric_weight: number;
  shipment_mode: string;
  fabric_content?: string;
  prev_fabric_content: string;
  fabric_code_name: string;
  lead_time_in_weeks: number;
  ro_lead_time_in_weeks: number;
  prev_vendor_name: string;
  prev_imu: number;
  target_price: number;
  imu: number;
  prev_fabric_name: string;
  prev_fabric_type: string;
  fabric_name: string;
  fabric_content_list?: CounterFabric[];
  // Legacy secondary fabric fields (kept for backward compatibility)
  contrast?: {
    contrastWeight?: number;
    contrastContent: CounterFabric[];
  };
  lining?: {
    liningWeight?: number;
    liningContent: CounterFabric[];
  };
  filling?: {
    fillingWeight?: number;
    fillingContent: CounterFabric[];
  };
  // Legacy-only types (not available for new entries, but supported for existing data)
  backing?: {
    backingWeight?: number;
    backingContent: CounterFabric[];
  };
  coating?: {
    coatingWeight?: number;
    coatingContent: CounterFabric[];
  };
  isFabricWeightNotApplicable?: boolean;
  hasPolyurethaneCoating?: boolean;
  secondaryFabrics?: SecondaryFabric[];
  isMultiGarment?: boolean;
  garments?: GarmentFabricDetails[];
}

export interface CounterQuoteRange {
  jr_cost: number;
  max_range: number;
  min_range: number;
  plus_cost: number | null;
  prev_jr_cost: number;
  prev_plus_cost: number;
  prev_weighted_cost: number;
  weighted_cost: number;
  baseline_jr_cost?: number;
  baseline_plus_cost?: number;
}

export enum ShipmentMode {
  LAND = 'land',
  AIR = 'air',
  SEA = 'sea',
}

export interface CounterSampleShippedRequest {
  counter_sample_shipped_at: string;
  counter_sample_tracking: string;
}

export interface PrevSampleReceivedRequest {
  prev_sample_received_at: string;
}

export interface UpdateContactRequest {
  vendor_contacts: {
    id: string;
    full_name: string;
    email: string;
  }[];
}

export interface GarmentFabricDetailsForAPI {
  garmentIndex: number;
  garmentPart: GarmentPart;
  fabricContentList?: CounterFabric[];
  fabricWeight?: string;
  fabricCodeName?: string;
  isFabricWeightNotApplicable?: boolean;
  secondaryFabrics?: Array<{
    type: SecondaryFabricType;
    instanceIndex: number;
    content: CounterFabric[];
    weight?: number | string;
  }>;
}

export interface CounterQuoteRequest {
  counter_quote: {
    ranges: Array<{
      min_range: number | '';
      max_range: number | '';
      jr_cost: number | '';
      plus_cost: number | '' | null;
      baseline_jr_cost?: number | '';
      baseline_plus_cost?: number | '' | null;
    }>;
    moq: number | string;
    mcq: number | string;
    lead_time_in_weeks: number | string;
    ro_lead_time_in_weeks: number | string;
    coo: string;
    shipment_mode: string;
    fabric_content?: string;
    fabric_weight: string;
    fabric_code_name: string;
    fabric_type: string;
    fabric_content_list?: CounterFabric[];
    isFabricWeightNotApplicable?: boolean;
    hasPolyurethaneCoating?: boolean;
    // Legacy secondary fabric fields
    contrast?: {
      contrastWeight?: number;
      contrastContent: CounterFabric[];
    };
    lining?: {
      liningWeight?: number;
      liningContent: CounterFabric[];
    };
    filling?: {
      fillingWeight?: number;
      fillingContent: CounterFabric[];
    };
    // Legacy-only types (not available for new entries, but supported for existing data)
    backing?: {
      backingWeight?: number;
      backingContent: CounterFabric[];
    };
    coating?: {
      coatingWeight?: number;
      coatingContent: CounterFabric[];
    };
    isMultiGarment?: boolean;
    garments?: GarmentFabricDetailsForAPI[];
  };
}

export interface CounterResponse {
  data: Counter[];
  total: number;
}

export interface CounterParams {
  page?: number;
  pageSize?: number;
  vendorContacts: number[];
  priorities: string[];
  quoteStatus: string[];
  prevSampleStatus: string[];
  counterSampleStatus: string[];
  sortedBy: string;
  sortOrder: string | null | undefined;
  counterIds: string[];
  search?: string;
  filters?: string;
}

export type QuantityItem = {
  min_range: number | '';
  max_range: number | '';
  jr_cost: number | '';
  plus_cost: number | '' | null;
  baseline_jr_cost?: number | '';
  baseline_plus_cost?: number | '' | null;
};

export enum CounterQuoteStatusToHide {
  FINAL_REVIEW = 'FinalReview',
  DISCUSS = 'Discuss',
  PENDING_COST = 'PendingCost',
  PENDING_QUALITY = 'PendingQuality',
  PENDING_MULTI = 'PendingMulti',
}

export enum CounterQuoteStatus {
  NOT_SENT = 'Quote Not Sent',
  SENT = 'Quote Sent',
  APPROVED = 'Quote Approved',
  REJECTED = 'Quote Rejected',
}

export enum CounterActivityEnum {
  'EDIT_COUNTER_QUOTE' = 'EDIT_COUNTER_QUOTE',
  'UPDATE_COUNTER_CONTACTS' = 'UPDATE_COUNTER_CONTACTS',
  'SEND_COUNTER_QUOTE_REQUEST_TO_VENDOR' = 'SEND_COUNTER_QUOTE_REQUEST_TO_VENDOR',
  'ADD_COUNTER_TARGET_PRICE' = 'ADD_COUNTER_TARGET_PRICE',
  'EDIT_COUNTER_TARGET_PRICE' = 'EDIT_COUNTER_TARGET_PRICE',
  'APPROVE_VENDOR_FOR_COUNTER' = 'APPROVE_VENDOR_FOR_COUNTER',
  'REJECT_VENDOR_FOR_COUNTER' = 'REJECT_VENDOR_FOR_COUNTER',
  'MARK_PREV_SAMPLE_AS_VENDOR_RECEIVED' = 'MARK_PREV_SAMPLE_AS_VENDOR_RECEIVED',
  'SHIP_PREV_SAMPLE_TO_VENDOR' = 'SHIP_PREV_SAMPLE_TO_VENDOR',
  'MARK_COUNTER_SAMPLE_AS_SHIPPED' = 'MARK_COUNTER_SAMPLE_AS_SHIPPED',
  'MARK_COUNTER_SAMPLE_AS_RECEIVED' = 'MARK_COUNTER_SAMPLE_AS_RECEIVED',
  'EDIT_VENDOR_COUNTER_QUOTE' = 'EDIT_VENDOR_COUNTER_QUOTE',
  'MARK_VENDOR_COUNTER_SAMPLE_AS_SHIPPED' = 'MARK_VENDOR_COUNTER_SAMPLE_AS_SHIPPED',
  'UPDATE_VENDOR_COUNTER_CONTACTS' = 'UPDATE_VENDOR_COUNTER_CONTACTS',
}

export type CounterContact = {
  id: string;
  full_name: string;
  email: string;
};

export type CounterActivityAuthor = {
  type: 'internal' | 'vp';
  id: string;
  name: string;
  email: string;
};

type EditCounterQuoteEvent = {
  event_type: CounterActivityEnum.EDIT_COUNTER_QUOTE;
  event_params: {
    previous_quote?: CounterQuote;
    new_quote: CounterQuote;
  };
};

type UpdateCounterContactsEvent = {
  event_type: CounterActivityEnum.UPDATE_COUNTER_CONTACTS;
  event_params: {
    added_fn_contacts: CounterContact[];
    removed_fn_contacts: CounterContact[];
    added_vendor_contacts: CounterContact[];
    removed_vendor_contacts: CounterContact[];
    added_primary_contact?: CounterContact;
    removed_primary_contact?: CounterContact;
  };
};

type SendCounterQuoteRequestEvent = {
  event_type: CounterActivityEnum.SEND_COUNTER_QUOTE_REQUEST_TO_VENDOR;
  event_params: {
    priority: string;
    vendor_contacts: CounterContact[];
  };
};

type AddTargetPriceEvent = {
  event_type: CounterActivityEnum.ADD_COUNTER_TARGET_PRICE;
  event_params: {
    new_price: number;
  };
};

type EditTargetPriceEvent = {
  event_type: CounterActivityEnum.EDIT_COUNTER_TARGET_PRICE;
  event_params: {
    previous_price: number;
    new_price: number;
  };
};

type ApproveVendorEvent = {
  event_type: CounterActivityEnum.APPROVE_VENDOR_FOR_COUNTER;
  event_params: {
    previous_status: string;
    new_status: string;
  };
};

type RejectVendorEvent = {
  event_type: CounterActivityEnum.REJECT_VENDOR_FOR_COUNTER;
  event_params: {
    reasons: string[];
  };
};

type MarkPrevSampleReceivedEvent = {
  event_type: CounterActivityEnum.MARK_PREV_SAMPLE_AS_VENDOR_RECEIVED;
  event_params: {
    vendor_received_at: Date;
  };
};

type ShipPrevSampleToVendorEvent = {
  event_type: CounterActivityEnum.SHIP_PREV_SAMPLE_TO_VENDOR;
  event_params: {
    tracking_number: string;
    shipped_at: Date;
  };
};

type MarkCounterSampleShippedEvent = {
  event_type: CounterActivityEnum.MARK_COUNTER_SAMPLE_AS_SHIPPED;
  event_params: {
    tracking_number: string;
    shipped_at: Date;
  };
};

type MarkCounterSampleReceivedEvent = {
  event_type: CounterActivityEnum.MARK_COUNTER_SAMPLE_AS_RECEIVED;
  event_params: {
    previous_status: string;
    new_status: string;
    received_at: Date;
  };
};

type EditVendorCounterQuoteEvent = {
  event_type: CounterActivityEnum.EDIT_VENDOR_COUNTER_QUOTE;
  event_params: {
    previous_quote?: CounterQuote;
    new_quote: CounterQuote;
  };
};

type MarkVendorSampleShippedEvent = {
  event_type: CounterActivityEnum.MARK_VENDOR_COUNTER_SAMPLE_AS_SHIPPED;
  event_params: {
    tracking_number: string;
    shipped_at: Date;
  };
};

type UpdateVendorCounterContactsEvent = {
  event_type: CounterActivityEnum.UPDATE_VENDOR_COUNTER_CONTACTS;
  event_params: {
    added_contacts: CounterContact[];
    removed_contacts: CounterContact[];
  };
};

export type CounterActivityEvent =
  | EditCounterQuoteEvent
  | UpdateCounterContactsEvent
  | SendCounterQuoteRequestEvent
  | AddTargetPriceEvent
  | EditTargetPriceEvent
  | ApproveVendorEvent
  | RejectVendorEvent
  | MarkPrevSampleReceivedEvent
  | ShipPrevSampleToVendorEvent
  | MarkCounterSampleShippedEvent
  | MarkCounterSampleReceivedEvent
  | EditVendorCounterQuoteEvent
  | MarkVendorSampleShippedEvent
  | UpdateVendorCounterContactsEvent;

export type CounterActivity = CounterActivityEvent & {
  event_id: string;
  counter_id: string;
  author: CounterActivityAuthor;
  created_at: string;
};

export const prevSampleStatusColorMapping = {
  'Shipped to You': 'purple',
  Received: 'green',
  Pending: 'neutral',
} as const;

export const counterSampleStatusColorMapping = {
  'Shipped to FN': 'purple',
  'Received by FN': 'green',
  Pending: 'neutral',
} as const;

export const counterQuoteStatusColorMapping = {
  'Quote Not Sent': 'neutral',
  'Quote Sent': 'primary',
  'Quote Approved': 'green',
  'Quote Rejected': 'red',
} as const;

export const counterQuoteStatusDisplayMapping = {
  RequestSentToVendor: 'Quote Not Sent',
  Received: 'Quote Sent',
  TargetPriceAdded: 'Quote Sent',
  Approved: 'Quote Approved',
  Rejected: 'Quote Rejected',
  VendorApprovalFailed: 'Quote Sent',
  VendorApprovalCompleted: 'Quote Approved',
} as const;

export const prevSampleStatusDisplayMapping = {
  Pending: 'Pending',
  Ordered: 'Pending',
  Received: 'Pending',
  NotAvailable: 'Pending',
  ShippedToVendor: 'Shipped to You',
  ReceivedByVendor: 'Received',
  samples_requested_to_library: 'Pending',
  samples_requested_to_dc: 'Pending',
  samples_ready_to_pickup_in_library: 'Pending',
  samples_in_transit_from_dc_to_hq: 'Pending',
} as const;

export const counterSampleStatusDisplayMapping = {
  Pending: 'Pending',
  Shipped: 'Shipped to FN',
  Received: 'Received by FN',
} as const;

export type FeedMessage = {
  type: 'message';
  message: ChatMessage;
};

export type CounterChatMessage = {
  sid: string;
  body: string;
  attributes: ChatMessageAttributes;
  dateCreated: string;
  author?: {
    sid: string;
    identity: string;
    friendlyName: string;
    isOnline: boolean;
    attributes: ChatUserAttributes;
  };
  media?: unknown[];
};

export type FeedBackMessage = {
  type: 'back-message';
  message: CounterChatMessage;
};

export type FeedActivity = {
  type: 'activity';
  activity: CounterActivity;
  isFirstTimeSubmission?: boolean;
};

export type FeedItem = FeedMessage | FeedBackMessage | FeedActivity;

export type CounterFiberContent = {
  id: string;
  name: string;
  percentage: number;
};

export type CounterFabricFormValues = {
  fabric_type: string;
  weight: number | string;
  fabric_code_name: string;
  fabric_content?: string;
  fabric_content_list?: CounterFiberContent[];
  isFabricWeightNotApplicable?: boolean;
  hasPolyurethaneCoating?: boolean;
  // Legacy secondary fabric fields
  contrast?: {
    contrastWeight?: number | string;
    contrastContent: CounterFiberContent[];
  };
  lining?: {
    liningWeight?: number | string;
    liningContent: CounterFiberContent[];
  };
  filling?: {
    fillingWeight?: number | string;
    fillingContent: CounterFiberContent[];
  };
  // Legacy-only types (not available for new entries, but supported for existing data)
  backing?: {
    backingWeight?: number | string;
    backingContent: CounterFiberContent[];
  };
  coating?: {
    coatingWeight?: number | string;
    coatingContent: CounterFiberContent[];
  };
  secondaryFabrics?: SecondaryFabric[];
  isMultiGarment?: boolean;
  garments?: GarmentFabricDetails[];
};

export type CounterQuoteFormValues = {
  type: 'range' | 'any';
  quantity: QuantityItem | Array<QuantityItem>;
  moq: number | string;
  mcq: number | string;
  lead_time: number | string;
  reorder_lead_time: number | string;
  coo: string;
  shipment_mode: string;
  isPlusCostNotApplicable?: boolean;
} & CounterFabricFormValues;
